import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "../../config/axios";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

    if (!formData.newPassword || !passwordRegex.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must be minimum 8 characters long and should contain atleast one uppercase, one lowercase and one special character.";
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const payload = {
        email,
        token,
        newPassword: formData.newPassword,
        confirmNewPassword: formData.confirmNewPassword,
      };
      await axios.post("/reset-password", payload);
      setSuccessMessage("Password reset successfully!");
      setFormData({
        newPassword: "",
        confirmNewPassword: "",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          background: "rgba(255,255,255,0.95)",
          borderRadius: "16px",
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            background: "#26A69A",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 4,
          }}
        >
          Reset Password
        </Typography>
        {(errorMessage || errors.email || errors.token) && (
          <div
            style={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <ErrorIcon style={{ marginRight: "8px" }} />
            {errorMessage || errors.email || errors.token}
          </div>
        )}
        {successMessage && (
          <div
            style={{
              background: "#4CAF50",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            {successMessage}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="New Password"
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleInputChange}
            margin="normal"
            error={!!errors.newPassword}
            helperText={
              errors.newPassword && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FF6F61",
                  }}
                >
                  {errors.newPassword}
                </span>
              )
            }
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirmNewPassword"
            type="password"
            value={formData.confirmNewPassword}
            onChange={handleInputChange}
            margin="normal"
            error={!!errors.confirmNewPassword}
            helperText={
              errors.confirmNewPassword && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FF6F61",
                  }}
                >
                  {errors.confirmNewPassword}
                </span>
              )
            }
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              background: "#26A69A",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.01)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                background: "#26A69A",
              },
              "&:disabled": { background: "grey.500", transform: "none" },
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Reset Password"}
          </Button>
        </form>
        <Link to="/login" style={{ textDecoration: "none", cursor: "pointer" }}>
          <Typography
            gutterBottom
            align="center"
            sx={{
              fontWeight: "bold",
              background: "#26A69A",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Back to login?
          </Typography>
        </Link>
      </Paper>
    </Box>
  );
}

export default ResetPassword;
